/**
 * Itens da licitação (17 §3.2, aba Itens): lista, cadastro manual, importação de planilha (Excel/CSV) e colagem de
 * células do Excel. O mapeamento de colunas é lembrado por órgão (importacao_mapeamento). Grava só as linhas sem erro.
 */
import { useMemo, useState } from "react";
import { useGetList, useNotify, useRefresh } from "react-admin";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/AddOutlined";
import ContentPasteIcon from "@mui/icons-material/ContentPasteOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import TableViewIcon from "@mui/icons-material/TableViewOutlined";
import TuneIcon from "@mui/icons-material/TuneOutlined";
import {
  CAMPOS_ITEM,
  encontrarCabecalho,
  lerItens,
  linhasColadas,
  numeroDaCelula,
  sugerirMapeamento,
  type CampoItem,
  type ItemImportado,
  type Mapeamento,
} from "@sobral/dominio";
import { useCelular } from "../componentes";
import { formatarMoeda, formatarNumero } from "../lib/formato";
import { situacaoFoge } from "../lib/pncp";
import { supabase } from "../lib/supabase";
import { ChipConferencia } from "./licitacao-leitura";

export interface ItemLicitacao {
  id: string;
  licitacao_id: string;
  lote: string | null;
  numero: number;
  descricao: string;
  qtd_min: number | null;
  qtd_max: number;
  unidade: string | null;
  valor_unit_estimado: number | null;
  valor_total_estimado: number | null;
  beneficio_me_epp: string | null;
  ncm: string | null;
  situacao_pncp: string | null;
  participar: boolean;
  observacao: string | null;
  conferencia_tr: string | null;
  conferencia_detalhe: unknown;
  conferido_em: string | null;
}

export const BENEFICIOS = [
  { id: "exclusivo", name: "Exclusivo ME/EPP" },
  { id: "cota_reservada", name: "Cota reservada ME/EPP" },
  { id: "subcontratacao", name: "Subcontratação ME/EPP" },
  { id: "nenhum", name: "Ampla concorrência" },
];

const ROTULO_CAMPO: Record<CampoItem, string> = {
  lote: "Lote",
  numero: "Nº do item",
  descricao: "Descrição",
  qtd_min: "Qtd. mínima",
  qtd_max: "Quantidade (máxima)",
  unidade: "Unidade",
  valor_unit_estimado: "Valor unitário estimado",
};
const OBRIGATORIOS: CampoItem[] = ["descricao", "qtd_max"];

/* ------------------------------------ Item manual ------------------------------------ */

interface CamposItem {
  lote: string;
  numero: string;
  descricao: string;
  qtd_min: string;
  qtd_max: string;
  unidade: string;
  valor_unit_estimado: string;
  beneficio_me_epp: string;
  participar: boolean;
  observacao: string;
}

const paraTexto = (v: number | null | undefined) => (v === null || v === undefined ? "" : String(v).replace(".", ","));

function DialogoItem({
  licitacaoId,
  item,
  proximoNumero,
  aoFechar,
}: {
  licitacaoId: string;
  item: ItemLicitacao | "novo" | null;
  proximoNumero: number;
  aoFechar: () => void;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [campos, setCampos] = useState<CamposItem | null>(null);
  const [chave, setChave] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const chaveAtual = item === null ? null : item === "novo" ? "novo" : item.id;
  if (chaveAtual !== chave) {
    setChave(chaveAtual);
    const i = item && item !== "novo" ? item : null;
    setCampos({
      lote: i?.lote ?? "",
      numero: String(i?.numero ?? proximoNumero),
      descricao: i?.descricao ?? "",
      qtd_min: paraTexto(i?.qtd_min),
      qtd_max: paraTexto(i?.qtd_max),
      unidade: i?.unidade ?? "",
      valor_unit_estimado: paraTexto(i?.valor_unit_estimado),
      beneficio_me_epp: i?.beneficio_me_epp ?? "",
      participar: i?.participar ?? true,
      observacao: i?.observacao ?? "",
    });
  }
  if (item === null || !campos) return null;
  const alterar = (c: Partial<CamposItem>) => setCampos((atual) => (atual ? { ...atual, ...c } : atual));

  const numero = numeroDaCelula(campos.numero);
  const qtdMax = numeroDaCelula(campos.qtd_max);
  const qtdMin = numeroDaCelula(campos.qtd_min);
  const valor = numeroDaCelula(campos.valor_unit_estimado);
  const erros = {
    numero: numero === null || Number.isNaN(numero) || !Number.isInteger(numero) || numero <= 0 ? "Informe um número inteiro." : null,
    descricao: campos.descricao.trim() ? null : "Informe a descrição.",
    qtd_max: qtdMax === null || Number.isNaN(qtdMax) || qtdMax <= 0 ? "Informe a quantidade." : null,
    qtd_min:
      qtdMin !== null && (Number.isNaN(qtdMin) || qtdMin < 0 || (qtdMax !== null && qtdMin > qtdMax)) ? "Mínima inválida ou maior que a máxima." : null,
    valor: valor !== null && (Number.isNaN(valor) || valor < 0) ? "Valor inválido." : null,
  };
  const valido = Object.values(erros).every((e) => e === null);

  async function gravar() {
    if (!campos || !valido) return;
    setGravando(true);
    const dados = {
      licitacao_id: licitacaoId,
      lote: campos.lote.trim() || null,
      numero,
      descricao: campos.descricao.trim(),
      qtd_min: qtdMin,
      qtd_max: qtdMax,
      unidade: campos.unidade.trim() || null,
      valor_unit_estimado: valor,
      beneficio_me_epp: campos.beneficio_me_epp || null,
      participar: campos.participar,
      observacao: campos.observacao.trim() || null,
    };
    const { error } =
      item === "novo"
        ? await supabase.from("licitacao_item").insert(dados)
        : await supabase.from("licitacao_item").update(dados).eq("id", (item as ItemLicitacao).id);
    setGravando(false);
    if (error) {
      notify(error.message.includes("licitacao_item_numero_unico") ? "Já existe um item com este número neste lote." : `Não foi possível gravar: ${error.message}`, {
        type: "error",
      });
      return;
    }
    notify(item === "novo" ? "Item incluído." : "Item atualizado.", { type: "success" });
    refresh();
    aoFechar();
  }

  return (
    <Dialog open onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="md">
      <DialogTitle>{item === "novo" ? "Adicionar item" : `Editar item ${item.numero}`}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField label="Lote" value={campos.lote} onChange={(e) => alterar({ lote: e.target.value })} fullWidth />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <TextField
              label="Nº do item"
              value={campos.numero}
              onChange={(e) => alterar({ numero: e.target.value })}
              error={Boolean(erros.numero)}
              helperText={erros.numero ?? " "}
              fullWidth
              required
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              select
              label="Benefício ME/EPP"
              value={campos.beneficio_me_epp}
              onChange={(e) => alterar({ beneficio_me_epp: e.target.value })}
              fullWidth
            >
              <MenuItem value="">Não informado</MenuItem>
              {BENEFICIOS.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={12}>
            <TextField
              label="Descrição"
              value={campos.descricao}
              onChange={(e) => alterar({ descricao: e.target.value })}
              error={Boolean(erros.descricao)}
              helperText={erros.descricao ?? " "}
              fullWidth
              multiline
              minRows={2}
              required
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Quantidade (máxima)"
              value={campos.qtd_max}
              onChange={(e) => alterar({ qtd_max: e.target.value })}
              error={Boolean(erros.qtd_max)}
              helperText={erros.qtd_max ?? " "}
              fullWidth
              required
              slotProps={{ htmlInput: { inputMode: "decimal" } }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Qtd. mínima"
              value={campos.qtd_min}
              onChange={(e) => alterar({ qtd_min: e.target.value })}
              error={Boolean(erros.qtd_min)}
              helperText={erros.qtd_min ?? "Opcional"}
              fullWidth
              slotProps={{ htmlInput: { inputMode: "decimal" } }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField label="Unidade" value={campos.unidade} onChange={(e) => alterar({ unidade: e.target.value })} fullWidth />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              label="Valor unitário estimado"
              value={campos.valor_unit_estimado}
              onChange={(e) => alterar({ valor_unit_estimado: e.target.value })}
              error={Boolean(erros.valor)}
              helperText={erros.valor ?? (valor !== null && !Number.isNaN(valor) ? formatarMoeda(valor) : "Opcional (sigiloso)")}
              fullWidth
              slotProps={{ htmlInput: { inputMode: "decimal" } }}
            />
          </Grid>
          <Grid size={12}>
            <TextField label="Observação" value={campos.observacao} onChange={(e) => alterar({ observacao: e.target.value })} fullWidth multiline />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              control={<Switch checked={campos.participar} onChange={(e) => alterar({ participar: e.target.checked })} />}
              label="Participar deste item"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void gravar()} disabled={gravando || !valido}>
          {gravando ? "Gravando…" : "Gravar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ------------------------------ Importação (planilha ou colagem) ------------------------------ */

type Origem = "planilha" | "colagem";

export interface Planilha {
  nome: string;
  abas: { nome: string; linhas: unknown[][] }[];
}

export async function lerPlanilha(arquivo: File): Promise<Planilha> {
  // SheetJS carregado sob demanda (só quando alguém importa).
  const XLSX = await import("xlsx");
  const livro = XLSX.read(await arquivo.arrayBuffer(), { type: "array" });
  return {
    nome: arquivo.name,
    abas: livro.SheetNames.map((nome) => {
      const aba = livro.Sheets[nome];
      return { nome, linhas: aba ? (XLSX.utils.sheet_to_json<unknown[]>(aba, { header: 1, raw: true, defval: "", blankrows: true }) as unknown[][]) : [] };
    }),
  };
}

/** Aplica o mapeamento lembrado (por nome de coluna) a um cabeçalho; campos sem correspondência ficam com a sugestão. */
function mapeamentoLembrado(cabecalho: string[], lembrado: Record<string, string> | null): Mapeamento {
  const sugestao = sugerirMapeamento(cabecalho);
  if (!lembrado) return sugestao;
  const resultado: Mapeamento = { ...sugestao };
  for (const campo of CAMPOS_ITEM) {
    const nome = lembrado[campo];
    if (!nome) continue;
    const indice = cabecalho.findIndex((c) => c.trim().toLowerCase() === nome.trim().toLowerCase());
    if (indice >= 0) resultado[campo] = indice;
  }
  // Uma coluna vale para um campo só: na disputa fica o campo que a sugestão pelo nome escolheu
  // (uma importação antiga gravou Lote e Nº do item na mesma coluna "Nº").
  const final: Mapeamento = {};
  for (const campo of CAMPOS_ITEM) {
    const indice = resultado[campo];
    if (indice === undefined) continue;
    const preferido = CAMPOS_ITEM.find((c) => resultado[c] === indice && sugestao[c] === indice);
    const jaUsado = CAMPOS_ITEM.some((c) => final[c] === indice);
    if (preferido ? preferido === campo : !jaUsado) final[campo] = indice;
  }
  return final;
}

/** Aba dos itens: a primeira com cabeçalho de descrição e quantidade; senão, a primeira com conteúdo. */
function abaDosItens(p: Planilha): number {
  const comCabecalho = p.abas.findIndex((a) => {
    const linha = (a.linhas[encontrarCabecalho(a.linhas)] ?? []).map((c) => String(c ?? ""));
    const mapa = sugerirMapeamento(linha);
    return mapa.descricao !== undefined && mapa.qtd_max !== undefined;
  });
  if (comCabecalho >= 0) return comCabecalho;
  return Math.max(0, p.abas.findIndex((a) => a.linhas.some((l) => l.some((c) => String(c ?? "").trim() !== ""))));
}

type Modo = "substituir" | "acrescentar";

function DialogoImportar({
  licitacaoId,
  pessoaId,
  origem,
  itensAtuais,
  aoFechar,
}: {
  licitacaoId: string;
  pessoaId: string;
  origem: Origem | null;
  itensAtuais: number;
  aoFechar: () => void;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const celular = useCelular();
  const [planilha, setPlanilha] = useState<Planilha | null>(null);
  const [aba, setAba] = useState(0);
  const [colado, setColado] = useState("");
  const [linhaCabecalho, setLinhaCabecalho] = useState(0);
  const [mapa, setMapa] = useState<Mapeamento>({});
  const [lembrado, setLembrado] = useState<Record<string, string> | null>(null);
  const [modo, setModo] = useState<Modo | null>(null);
  const [ajustar, setAjustar] = useState(false);
  const [verProblemas, setVerProblemas] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [lendo, setLendo] = useState(false);
  const [chave, setChave] = useState<Origem | null>(null);

  if (origem !== chave) {
    setChave(origem);
    setPlanilha(null);
    setAba(0);
    setColado("");
    setLinhaCabecalho(0);
    setMapa({});
    setModo(null);
    setAjustar(false);
    setVerProblemas(false);
    if (origem) {
      void supabase
        .from("importacao_mapeamento")
        .select("mapeamento")
        .eq("pessoa_id", pessoaId)
        .maybeSingle()
        .then(({ data }) => setLembrado((data?.mapeamento as Record<string, string> | undefined) ?? null));
    }
  }

  const linhas: unknown[][] = useMemo(
    () => (origem === "planilha" ? (planilha?.abas[aba]?.linhas ?? []) : linhasColadas(colado)),
    [origem, planilha, aba, colado],
  );
  const cabecalho = useMemo(() => (linhas[linhaCabecalho] ?? []).map((c) => String(c ?? "").trim()), [linhas, linhaCabecalho]);
  const itens: ItemImportado[] = useMemo(() => (linhas.length > 0 ? lerItens(linhas, linhaCabecalho, mapa) : []), [linhas, linhaCabecalho, mapa]);
  const validos = itens.filter((i) => i.erros.length === 0);
  const problemas = itens.filter((i) => i.erros.length > 0);
  const faltaColuna = OBRIGATORIOS.some((c) => mapa[c] === undefined);
  const temLote = validos.some((i) => i.lote);
  // Os ajustes abrem sozinhos quando a leitura automática não deu conta.
  const ajustesAbertos = ajustar || faltaColuna || validos.length === 0;
  const precisaModo = itensAtuais > 0 && modo === null;

  function prepararLinhas(novas: unknown[][]) {
    const cab = encontrarCabecalho(novas);
    setLinhaCabecalho(cab);
    setMapa(mapeamentoLembrado((novas[cab] ?? []).map((c) => String(c ?? "")), lembrado));
  }

  async function receberArquivo(arquivo: File | undefined) {
    if (!arquivo) return;
    setLendo(true);
    try {
      const p = await lerPlanilha(arquivo);
      setPlanilha(p);
      const indice = abaDosItens(p);
      setAba(indice);
      prepararLinhas(p.abas[indice]?.linhas ?? []);
    } catch (e) {
      notify(`Não foi possível ler a planilha: ${(e as Error).message}`, { type: "error" });
    } finally {
      setLendo(false);
    }
  }

  async function gravar() {
    if (validos.length === 0 || precisaModo) return;
    setGravando(true);
    try {
      if (modo === "substituir") {
        const { error } = await supabase.from("licitacao_item").delete().eq("licitacao_id", licitacaoId);
        if (error) throw new Error(error.message);
      }
      const { error } = await supabase.from("licitacao_item").insert(
        validos.map((i) => ({
          licitacao_id: licitacaoId,
          lote: i.lote,
          numero: i.numero,
          descricao: i.descricao,
          qtd_min: i.qtd_min,
          qtd_max: i.qtd_max,
          unidade: i.unidade,
          valor_unit_estimado: i.valor_unit_estimado,
        })),
      );
      if (error) {
        throw new Error(
          error.message.includes("licitacao_item_numero_unico")
            ? "Há itens com o mesmo número de itens já cadastrados. Escolha \"Substituir\" ou ajuste a numeração."
            : error.message,
        );
      }
      // Lembra o mapeamento por órgão (nome das colunas).
      const porNome: Record<string, string> = {};
      for (const campo of CAMPOS_ITEM) {
        const indice = mapa[campo];
        if (indice !== undefined && cabecalho[indice]) porNome[campo] = cabecalho[indice];
      }
      await supabase.from("importacao_mapeamento").upsert({ pessoa_id: pessoaId, mapeamento: porNome }, { onConflict: "pessoa_id" });
      notify(
        problemas.length > 0
          ? `${validos.length} item(ns) gravado(s). ${problemas.length} linha(s) com problema não foram gravadas.`
          : `${validos.length} item(ns) gravado(s).`,
        { type: problemas.length > 0 ? "warning" : "success" },
      );
      refresh();
      aoFechar();
    } catch (e) {
      notify(`Não foi possível gravar: ${(e as Error).message}`, { type: "error" });
    } finally {
      setGravando(false);
    }
  }

  if (!origem) return null;
  const temDados = linhas.length > 0;
  const nomeAba = planilha && planilha.abas.length > 1 ? planilha.abas[aba]?.nome.trim() : undefined;

  return (
    <Dialog open onClose={gravando ? undefined : aoFechar} fullWidth maxWidth="lg" fullScreen={celular}>
      <DialogTitle>{origem === "planilha" ? "Importar itens de planilha" : "Colar itens do Excel"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {origem === "planilha" ? (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
              <Button component="label" variant={planilha ? "outlined" : "contained"} startIcon={<TableViewIcon />} disabled={lendo || gravando}>
                {planilha ? "Trocar planilha" : "Escolher planilha"}
                <input type="file" hidden accept=".xlsx,.xls,.ods,.csv" onChange={(e) => void receberArquivo(e.target.files?.[0])} />
              </Button>
              {lendo && <Typography variant="body2">Lendo a planilha…</Typography>}
              {planilha && !lendo && (
                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                  {planilha.nome}
                </Typography>
              )}
            </Stack>
          ) : (
            <TextField
              label="Cole aqui as células copiadas do Excel (com a linha de cabeçalho)"
              value={colado}
              onChange={(e) => {
                setColado(e.target.value);
                prepararLinhas(linhasColadas(e.target.value));
              }}
              multiline
              minRows={4}
              maxRows={10}
              fullWidth
              slotProps={{ htmlInput: { style: { fontFamily: "monospace", fontSize: 12 } } }}
            />
          )}

          {temDados && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {validos.length === 0
                    ? "Nenhum item encontrado"
                    : `${validos.length} ${validos.length === 1 ? "item encontrado" : "itens encontrados"}${nomeAba ? ` na aba ${nomeAba}` : ""}`}
                </Typography>
                {!faltaColuna && validos.length > 0 && (
                  <Button size="small" variant="outlined" startIcon={<TuneIcon />} onClick={() => setAjustar((a) => !a)}>
                    {ajustar ? "Ocultar ajustes" : "Ajustar colunas"}
                  </Button>
                )}
              </Stack>

              <Collapse in={ajustesAbertos}>
                <Stack spacing={2} sx={{ pt: 0.5 }}>
                  {faltaColuna && (
                    <Alert severity="warning">Escolha abaixo as colunas da descrição e da quantidade.</Alert>
                  )}
                  <Grid container spacing={2}>
                    {planilha && planilha.abas.length > 1 && (
                      <Grid size={{ xs: 12, md: 3 }} sx={{ minWidth: 0 }}>
                        <TextField
                          select
                          size="small"
                          label="Aba"
                          value={aba}
                          onChange={(e) => {
                            const indice = Number(e.target.value);
                            setAba(indice);
                            prepararLinhas(planilha.abas[indice]?.linhas ?? []);
                          }}
                          fullWidth
                        >
                          {planilha.abas.map((a, i) => (
                            <MenuItem key={a.nome} value={i}>
                              {a.nome}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, md: planilha && planilha.abas.length > 1 ? 9 : 12 }} sx={{ minWidth: 0 }}>
                      <TextField
                        select
                        size="small"
                        label="Linha do cabeçalho"
                        value={linhaCabecalho}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          setLinhaCabecalho(n);
                          setMapa(mapeamentoLembrado((linhas[n] ?? []).map((c) => String(c ?? "")), lembrado));
                        }}
                        fullWidth
                        helperText={lembrado ? "Colunas sugeridas pela última importação deste órgão." : "Colunas sugeridas pelos nomes do cabeçalho."}
                      >
                        {linhas.slice(0, 30).map((l, i) => (
                          <MenuItem key={i} value={i}>
                            {`Linha ${i + 1}: ${l
                              .map((c) => String(c ?? "").trim())
                              .filter(Boolean)
                              .join(" | ")
                              .slice(0, 60)}`}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1}>
                    {CAMPOS_ITEM.map((campo) => (
                      <Grid key={campo} size={{ xs: 6, sm: 4, md: 12 / 7 }} sx={{ minWidth: 0 }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label={ROTULO_CAMPO[campo]}
                          value={mapa[campo] ?? ""}
                          error={OBRIGATORIOS.includes(campo) && mapa[campo] === undefined}
                          onChange={(e) =>
                            setMapa((m) => {
                              const { [campo]: _anterior, ...resto } = m;
                              return e.target.value === "" ? resto : { ...resto, [campo]: Number(e.target.value) };
                            })
                          }
                        >
                          <MenuItem value="">Não usar</MenuItem>
                          {cabecalho.map((c, i) =>
                            // Coluna já usada por outro campo não aparece (uma coluna, um campo).
                            CAMPOS_ITEM.some((outro) => outro !== campo && mapa[outro] === i) ? null : (
                              <MenuItem key={i} value={i}>
                                {c || `Coluna ${i + 1}`}
                              </MenuItem>
                            ),
                          )}
                        </TextField>
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              </Collapse>

              {itensAtuais > 0 && validos.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    {itensAtuais === 1 ? "Esta licitação já tem 1 item. O que fazer com ele?" : `Esta licitação já tem ${itensAtuais} itens. O que fazer com eles?`}
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={modo}
                    onChange={(_e, valor: Modo | null) => {
                      if (valor) setModo(valor);
                    }}
                    sx={{ flexWrap: "wrap" }}
                  >
                    <ToggleButton value="substituir">{itensAtuais === 1 ? "Substituir pelo da planilha" : "Substituir pelos da planilha"}</ToggleButton>
                    <ToggleButton value="acrescentar">Manter e acrescentar</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              )}

              {problemas.length > 0 && (
                <Alert
                  severity="warning"
                  action={
                    <Button color="inherit" size="small" onClick={() => setVerProblemas((v) => !v)}>
                      {verProblemas ? "Ocultar" : "Ver"}
                    </Button>
                  }
                >
                  {problemas.length === 1 ? "1 linha com problema não será gravada." : `${problemas.length} linhas com problema não serão gravadas.`}
                  <Collapse in={verProblemas}>
                    <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }}>
                      {problemas.map((i) => (
                        <li key={i.linha}>
                          <Typography variant="body2">
                            {`Linha ${i.linha}${i.numero !== null ? ` (item ${i.numero})` : ""}: ${i.erros.join("; ")}`}
                            {i.descricao ? ` — ${i.descricao.slice(0, 80)}` : ""}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  </Collapse>
                </Alert>
              )}

              {validos.length > 0 && (
                <TableContainer sx={{ maxHeight: 420, overflow: "auto" }}>
                  <Table size="small" stickyHeader sx={{ minWidth: celular ? 600 : undefined }}>
                    <TableHead>
                      <TableRow>
                        {temLote && <TableCell>Lote</TableCell>}
                        <TableCell>Item</TableCell>
                        <TableCell>Descrição</TableCell>
                        <TableCell align="right">Qtd.</TableCell>
                        <TableCell>Unid.</TableCell>
                        <TableCell align="right">Valor unit.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validos.map((i) => (
                        <TableRow key={i.linha}>
                          {temLote && <TableCell>{i.lote ?? ""}</TableCell>}
                          <TableCell>{i.numero ?? ""}</TableCell>
                          <TableCell sx={{ maxWidth: 520 }}>
                            <Typography variant="body2" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                              {i.descricao}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {formatarNumero(i.qtd_max)}
                            {i.qtd_min !== null ? ` (mín. ${formatarNumero(i.qtd_min)})` : ""}
                          </TableCell>
                          <TableCell>{i.unidade ?? ""}</TableCell>
                          <TableCell align="right">{formatarMoeda(i.valor_unit_estimado)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
          {!temDados && origem === "colagem" && (
            <Alert severity="info">No Excel, selecione o cabeçalho e as linhas dos itens, copie (Ctrl+C) e cole acima (Ctrl+V).</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={aoFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Tooltip title={precisaModo && validos.length > 0 ? "Escolha se substitui ou mantém os itens atuais." : ""}>
          <span>
            <Button variant="contained" onClick={() => void gravar()} disabled={gravando || validos.length === 0 || faltaColuna || precisaModo}>
              {gravando ? "Gravando…" : validos.length === 1 ? "Gravar 1 item" : `Gravar ${validos.length} itens`}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}

/* ------------------------------------ Aba Itens ------------------------------------ */

export function ItensLicitacao({ licitacaoId, pessoaId }: { licitacaoId: string; pessoaId: string }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const celular = useCelular();
  const [edicao, setEdicao] = useState<ItemLicitacao | "novo" | null>(null);
  const [importar, setImportar] = useState<Origem | null>(null);
  const [excluindo, setExcluindo] = useState<string | null>(null);
  const { data: itens = [], isPending } = useGetList<ItemLicitacao>("licitacao_item", {
    filter: { licitacao_id: licitacaoId },
    pagination: { page: 1, perPage: 2000 },
    sort: { field: "numero", order: "ASC" },
  });
  const ordenados = useMemo(
    () => [...itens].sort((a, b) => (a.lote ?? "").localeCompare(b.lote ?? "", "pt-BR", { numeric: true }) || a.numero - b.numero),
    [itens],
  );
  const total = itens.reduce((s, i) => s + Number(i.valor_total_estimado ?? 0), 0);
  const temLote = itens.some((i) => i.lote);
  const proximo = itens.reduce((m, i) => Math.max(m, i.numero), 0) + 1;

  async function excluir(item: ItemLicitacao) {
    setExcluindo(item.id);
    const { error } = await supabase.from("licitacao_item").delete().eq("id", item.id);
    setExcluindo(null);
    if (error) notify(`Não foi possível excluir: ${error.message}`, { type: "error" });
    else {
      notify(`Item ${item.numero} excluído.`, { type: "success" });
      refresh();
    }
  }

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {itens.length} item(ns) · valor estimado total {formatarMoeda(total)}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" startIcon={<TableViewIcon />} onClick={() => setImportar("planilha")}>
            Importar planilha
          </Button>
          <Button variant="outlined" startIcon={<ContentPasteIcon />} onClick={() => setImportar("colagem")}>
            Colar do Excel
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEdicao("novo")}>
            Adicionar item
          </Button>
        </Stack>
      </Stack>
      {!isPending && itens.length === 0 ? (
        <Alert severity="info">
          Nenhum item cadastrado. Importe a planilha do edital, cole as células do Excel ou adicione item a item.
        </Alert>
      ) : (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: celular ? 700 : undefined }}>
            <TableHead>
              <TableRow>
                {temLote && <TableCell>Lote</TableCell>}
                <TableCell>Item</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell align="right">Qtd.</TableCell>
                <TableCell>Unid.</TableCell>
                <TableCell align="right">Valor unit. est.</TableCell>
                <TableCell align="right">Total est.</TableCell>
                <TableCell>ME/EPP</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {ordenados.map((i) => (
                <TableRow key={i.id} hover sx={{ opacity: i.participar ? 1 : 0.55 }}>
                  {temLote && <TableCell>{i.lote ?? ""}</TableCell>}
                  <TableCell>{i.numero}</TableCell>
                  <TableCell sx={{ maxWidth: 520 }}>
                    <Typography variant="body2" sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {i.descricao}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }} flexWrap="wrap" useFlexGap>
                      {!i.participar && <Chip size="small" variant="outlined" label="Não participar" />}
                      {situacaoFoge(i.situacao_pncp) && (
                        <Chip size="small" color="warning" variant="outlined" label={`PNCP: ${i.situacao_pncp}`} />
                      )}
                      <ChipConferencia item={i} />
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {formatarNumero(i.qtd_max)}
                    {i.qtd_min !== null && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        mín. {formatarNumero(i.qtd_min)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{i.unidade ?? ""}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {i.valor_unit_estimado === null ? "Sigiloso" : formatarMoeda(i.valor_unit_estimado)}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {formatarMoeda(i.valor_total_estimado)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{BENEFICIOS.find((b) => b.id === i.beneficio_me_epp)?.name ?? "—"}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="Editar item">
                      <IconButton size="small" onClick={() => setEdicao(i)} aria-label="Editar item">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir item">
                      <span>
                        <IconButton size="small" onClick={() => void excluir(i)} disabled={excluindo === i.id} aria-label="Excluir item">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={temLote ? 6 : 5} align="right">
                  Total estimado
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>
                  {formatarMoeda(total)}
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
      <DialogoItem licitacaoId={licitacaoId} item={edicao} proximoNumero={proximo} aoFechar={() => setEdicao(null)} />
      <DialogoImportar licitacaoId={licitacaoId} pessoaId={pessoaId} origem={importar} itensAtuais={itens.length} aoFechar={() => setImportar(null)} />
    </Box>
  );
}
